/**
 * Get style recommendations based on face shape
 * @param {string} faceShape - The classified face shape
 * @returns {Object} Recommendations for glasses, hairstyles, and clothing
 */
export const getRecommendationsForShape = (faceShape) => {
  // Default recommendations if face shape is not recognized
  const defaultRecommendations = {
    description: "We couldn't determine your face shape with confidence. Here are some general recommendations.",
    glasses: ["versatile frames like oval or rectangle", "balanced proportion frames", "try different styles to see what suits you"],
    hairStyles: ["medium-length layered cuts", "styles that frame your face", "consult with a professional stylist"],
    clothingColors: ["experiment with different necklines", "balanced patterns and colors", "find what makes you feel confident"],
    icons: {
      glasses: "M15 8a2 2 0 1 0-4 0m4 0a2 2 0 1 1-4 0m4 0h6m-12 0h6m4 4H3m14-4a4 4 0 0 1 0 8",
      hairStyles: "M18 20a6 6 0 0 0-12 0m12-11A3 3 0 0 0 6 9c0 1.038 1.277 1.928 2 2.5C4.5 13 4 15.893 4 17.5a6.5 6.5 0 0 0 13 0c0-1.607-.5-4.5-4-6 .723-.572 2-1.462 2-2.5a3 3 0 0 0-3-3",
      clothingColors: "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm12 7c0 1.67 1.34 3 3 3h6V9h-6c-1.66 0-3 1.34-3 3"
    }
  };
  
  // Face shape specific recommendations
  const recommendations = {
    oval: {
      description: "The oval face is considered the ideal shape because of its balanced proportions.",
      glasses: [
        "aviator styles",
        "rectangular frames",
        "wayfarer shapes",
        "square frames",
        "most frame styles work well"
      ],
      hairStyles: [
        "virtually any hairstyle",
        "long layers",
        "medium-length cuts",
        "pixie cuts",
        "bangs of any style"
      ],
      clothingColors: [
        "most colors complement you well",
        "jewel tones to enhance your features",
        "pastels for a soft look",
        "neutral colors for elegance",
        "versatile necklines"
      ],
      icons: {
        glasses: "M15 8a2 2 0 1 0-4 0m4 0a2 2 0 1 1-4 0m4 0h6m-12 0h6m4 4H3m14-4a4 4 0 0 1 0 8",
        hairStyles: "M11 19.25c0-2.071 4-3.5 4-7.5a4 4 0 0 0-8 0c0 4 4 5.429 4 7.5zm5-7.5c-1.5 0-3 1.5-3 3m-6-3c1.5 0 3 1.5 3 3",
        clothingColors: "M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm9-2v12m-7-6h14"
      }
    },
    round: {
      description: "Round face shapes have curved lines with the width and length in similar proportions and no sharp angles.",
      glasses: [
        "rectangular frames",
        "angular shapes",
        "geometric styles",
        "wayfarers",
        "cat-eye frames"
      ],
      hairStyles: [
        "layered cuts with height at the crown",
        "side-swept bangs",
        "long styles below the chin",
        "asymmetrical cuts",
        "pixie cuts with volume on top"
      ],
      clothingColors: [
        "vertical stripes or patterns",
        "v-necks to elongate the face",
        "darker colors for slimming effect",
        "monochromatic outfits",
        "statement earrings to draw eye upward"
      ],
      icons: {
        glasses: "M7 9H3v2h4v-2zm14 0h-4v2h4v-2zm-3-5a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm-8 0a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
        hairStyles: "M12 21c-3.5 0-6-3-6-7 0-2.76 2.24-5 5-5s5 2.24 5 5c0 1-1 3-2 3 0-1 .5-3-2-3-3 0-3.5 3.5-3.5 5.5L12 21zm1-13c3-2 3-5 3-5s-7 0-7 5c0 2 2 3 3 3 1.5 0 1-3 1-3z",
        clothingColors: "M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm7 0v12m0-6h10"
      }
    },
    square: {
      description: "Square faces have a strong jaw and a broad forehead, with the width and length in similar proportions.",
      glasses: [
        "round frames",
        "oval shapes",
        "thin rims",
        "cat-eye styles",
        "frames with curved edges"
      ],
      hairStyles: [
        "soft layers",
        "waves or curls",
        "side-swept bangs",
        "textured styles",
        "longer bobs with movement"
      ],
      clothingColors: [
        "soft, rounded patterns",
        "scoop or round necklines",
        "delicate fabrics",
        "rounded collars",
        "soft color palettes"
      ],
      icons: {
        glasses: "M12 6a3 3 0 0 1 3 3v1h3v2h-3v1a3 3 0 0 1-6 0v-1H6v-2h3v-1a3 3 0 0 1 3-3z",
        hairStyles: "M12 21c-3 0-6-3-6-7 0-2.76 2.24-5 5-5s5 2.24 5 5c0 1-1 3-2 3 0-1 .5-3-2-3-3 0-3.5 3.5-3.5 5.5L12 21zm0-15c-2 0-2-2-2-2s4-1 4 1c0 1-2 1-2 1z",
        clothingColors: "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm2 6h14M9 6v12m6-12v12"
      }
    },
    heart: {
      description: "Heart-shaped faces have a wider forehead and cheekbones with a narrow jawline and chin.",
      glasses: [
        "bottom-heavy frames",
        "oval shapes",
        "light-colored or rimless styles",
        "thin frames",
        "low-set temples"
      ],
      hairStyles: [
        "chin-length or longer cuts",
        "side-parted styles",
        "layers that add width at the jawline",
        "full, bouncy styles",
        "waves around the jaw"
      ],
      clothingColors: [
        "high necklines",
        "boat necks or scoop necks",
        "chokers to highlight the neck",
        "statement necklaces",
        "darker colors on top, lighter on bottom"
      ],
      icons: {
        glasses: "M15 5.5c1.74 0 3.41.81 4.5 2.09C16.41 9.77 14.74 10.5 13 10.5s-3.41-.73-4.5-2.09C9.59 6.31 11.26 5.5 13 5.5zm0 2.5c.83 0 1.5-.67 1.5-1.5S13.83 5 13 5s-1.5.67-1.5 1.5.67 1.5 1.5 1.5z",
        hairStyles: "M12 21c-3 0-6-3-6-7 0-2.76 2.24-5 5-5s5 2.24 5 5c0 4-4 7-7 7zm-2-7c-1 2 1 4 3 4s4-2 3-4m-3-5c-2-3-3-5 1-5 3 0 4 3 2 5",
        clothingColors: "M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm2 0v4h14V8z"
      }
    },
    diamond: {
      description: "Diamond faces have narrow foreheads and jawlines with cheekbones at the widest point.",
      glasses: [
        "top-heavy frames",
        "cat-eye styles",
        "oval shapes",
        "rimless glasses",
        "frames with detailing on the upper part"
      ],
      hairStyles: [
        "hairstyles with volume at the forehead",
        "textured bangs",
        "chin-length bobs",
        "styles that add width at jawline",
        "face-framing layers"
      ],
      clothingColors: [
        "boat necklines",
        "off-shoulder tops",
        "balanced patterns",
        "statement earrings",
        "collared shirts"
      ],
      icons: {
        glasses: "M8 4H6l-1 4-2 1 2 1 1 4h2m8 0h2l1-4 2-1-2-1-1-4h-2m-4-1v6m1 0H11m1 8v-3",
        hairStyles: "M12 21c-3 0-6-3-6-7 0-2.76 2.24-5 5-5s5 2.24 5 5c0 4-3 7-6 7zm-1-10c-2-3 0-6 2-6 3 0 3 4 1 6m-2 2c1 1 3 1 4 0",
        clothingColors: "M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm9-2l-9 6 9 6 9-6z"
      }
    },
    oblong: {
      description: "Oblong faces are longer than they are wide with a long straight cheek line.",
      glasses: [
        "oversized frames",
        "round shapes",
        "decorative or contrasting temples",
        "wide frames",
        "bold styles"
      ],
      hairStyles: [
        "layered cuts with volume on the sides",
        "bangs to shorten the face",
        "curls and waves",
        "short to medium-length styles",
        "side parts"
      ],
      clothingColors: [
        "horizontal patterns",
        "crew necks",
        "turtlenecks",
        "chokers and shorter necklaces",
        "boat necks to widen"
      ],
      icons: {
        glasses: "M6 4h12m-6 0v6m-4.5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm9 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
        hairStyles: "M12 21c-3 0-6-3-6-7 0-2.76 2.24-5 5-5s5 2.24 5 5c0 4-4 7-8 7zm1-8c-1-3 1-8 3-8s2 4 0 8m-6 0c1-3-1-8-3-8s-2 4 0 8",
        clothingColors: "M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm4 1h10m-10 3h10m-10 3h10"
      }
    }
  };
  
  return recommendations[faceShape] || defaultRecommendations;
};

/**
 * Get celebrity examples for each face shape
 * @param {string} faceShape - The face shape
 * @returns {Array} List of celebrities with that face shape
 */
export const getCelebrityExamples = (faceShape) => {
  const celebrities = {
    oval: [
      "Jessica Alba",
      "Deepika Padukone",
      "George Clooney",
      "Kate Middleton",
      "Hrithik Roshan"
    ],
    round: [
      "Selena Gomez",
      "Alia Bhatt",
      "Leonardo DiCaprio",
      "Chrissy Teigen",
      "Aamir Khan"
    ],
    square: [
      "Angelina Jolie",
      "Kareena Kapoor Khan",
      "Brad Pitt",
      "Priyanka Chopra",
      "Akshay Kumar"
    ],
    heart: [
      "Ryan Gosling",
      "Aishwarya Rai Bachchan",
      "Zendaya",
      "Shah Rukh Khan",
      "Katrina Kaif"
    ],
    diamond: [
      "Jennifer Lopez",
      "Anushka Sharma",
      "Robert Pattinson",
      "Sonam Kapoor",
      "Ranveer Singh"
    ],
    oblong: [
      "Sarah Jessica Parker",
      "Vidya Balan",
      "Adam Driver",
      "Amitabh Bachchan",
      "Shilpa Shetty"
    ]
  };
  
  return celebrities[faceShape] || [];
};

/**
 * Get complementary colors for a face shape
 * @param {string} faceShape - The face shape
 * @returns {Object} Complementary color recommendations
 */
export const getComplementaryColors = (faceShape) => {
  const colorRecommendations = {
    oval: {
      best: ["All colors work well", "Balanced tones", "Vibrant colors for statement looks"],
      avoid: ["None in particular"],
      colorPalette: ["#4F86F7", "#6B5B95", "#E94B3C", "#2E8B57", "#DAA520"]
    },
    round: {
      best: ["Deep colors", "Dark shades", "Cool tones", "Navy blue", "Burgundy"],
      avoid: ["Large, round patterns", "Light pastels that add volume"],
      colorPalette: ["#483D8B", "#800000", "#2F4F4F", "#191970", "#556B2F"]
    },
    square: {
      best: ["Soft colors", "Pastels", "Muted tones", "Lavender", "Peach"],
      avoid: ["Stark, high-contrast patterns", "Very bold colors"],
      colorPalette: ["#E6E6FA", "#FFE4E1", "#F0FFF0", "#E0FFFF", "#FFDAB9"]
    },
    heart: {
      best: ["Light colors on bottom", "Darker shades on top", "Emerald green", "Ruby red"],
      avoid: ["Heavy patterns around neck", "Oversized collars"],
      colorPalette: ["#006400", "#8B0000", "#000080", "#4B0082", "#3C1414"]
    },
    diamond: {
      best: ["Bright colors", "Bold patterns at forehead or jawline", "Turquoise", "Coral"],
      avoid: ["Heavy patterns at cheeks", "Busy prints across the face"],
      colorPalette: ["#40E0D0", "#FF7F50", "#BA55D3", "#32CD32", "#FF4500"]
    },
    oblong: {
      best: ["Horizontal patterns", "High necklines", "Rich colors", "Chocolate brown", "Teal"],
      avoid: ["Vertical stripes", "Deep V-necks", "Monochromatic looks"],
      colorPalette: ["#008080", "#8B4513", "#4682B4", "#808000", "#CD853F"]
    }
  };
  
  return colorRecommendations[faceShape] || { best: [], avoid: [], colorPalette: ["#000000", "#FFFFFF"] };
};

/**
 * Get traditional outfit recommendations based on face shape
 * @param {string} faceShape - The face shape
 * @returns {Object} Traditional outfit recommendations
 */
export const getTraditionalOutfitRecommendations = (faceShape) => {
  const traditionalRecommendations = {
    oval: {
      indian: [
        "Any saree style works well",
        "Anarkali suits with balanced proportions",
        "Medium-sized bindis",
        "Classic jewellery styles"
      ],
      western: [
        "A-line dresses",
        "Classic blazers and suits",
        "Versatile necklines",
        "Mid-length skirts"
      ]
    },
    round: {
      indian: [
        "V-neck blouses with sarees",
        "Vertical patterns on kurtas",
        "Lehengas with vertical details",
        "Long, thin earrings to elongate"
      ],
      western: [
        "Empire waist dresses",
        "Vertical pinstripe suits",
        "V-neck or scoop neck tops",
        "Straight leg pants"
      ]
    },
    square: {
      indian: [
        "Soft draping sarees",
        "Round or scoop neck blouses",
        "Flowy fabrics for suits and lehengas",
        "Circular or floral design jewellery"
      ],
      western: [
        "Wrap dresses",
        "Soft, rounded lapels on jackets",
        "Cowl necklines",
        "Flowy fabrics"
      ]
    },
    heart: {
      indian: [
        "High neckline blouses",
        "Sarees with embellishments at the hip",
        "A-line lehengas",
        "Dupatta styles that add volume at lower body"
      ],
      western: [
        "Boat neck tops",
        "A-line skirts",
        "Wide-leg trousers",
        "Statement necklaces"
      ]
    },
    diamond: {
      indian: [
        "Boat neck blouses",
        "Off-shoulder saree styles",
        "Medium-weight fabrics",
        "Bold earrings to balance cheekbones"
      ],
      western: [
        "Strapless dresses",
        "Padded shoulders",
        "Peplum tops",
        "Structured blazers"
      ]
    },
    oblong: {
      indian: [
        "Broad border sarees",
        "High-neck blouses",
        "Choker style necklaces",
        "Horizontal patterns on kurtas"
      ],
      western: [
        "Turtlenecks",
        "Chunky necklaces",
        "Boatneck tops",
        "Cropped jackets"
      ]
    }
  };
  
  return traditionalRecommendations[faceShape] || { indian: [], western: [] };
};

export default {
  getRecommendationsForShape,
  getCelebrityExamples,
  getComplementaryColors,
  getTraditionalOutfitRecommendations
};